import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import {
  IconButton,
  Select,
  MenuItem,
  Box,
  Chip,
  Tooltip,
  Typography,
  Popover,
} from "@mui/material";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import dayjs from "dayjs";
import "./JiraIncidentTable.scss";
import {
  CLEAR_JIRA_INCIDENT_TABLE_FILTERS,
  GET_TABLE_DATA_JIRA,
  SET_JIRA_INCIDENT_TABLE_FILTER,
  SET_JIRA_INCIDENT_TABLE_FILTER_OPTIONS,
} from "./mdrConstants";
import JiraColumnFilterDropdown from "./JiraColumnFilterDropdown";
import { renderHeaderWithFilter } from "../NetSpace/NetSpaceUtils";
import { useMediaQuery } from "material-ui-core";
import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import IncidentSidebar from "../IncidentSidebar/IncidentSidebar";
import { FilterGroup } from "./MDRCommonComponents";

export const statusFilter = ["Open", "Closed"];

const useStyles = makeStyles({
  paper: {
    background: "white",
  },
});

const JiraIncidentTable = () => {
  const dispatch = useDispatch();
  const isSmall = useMediaQuery("(max-width:1600px)");
  const classes = useStyles();
  const d = new Date();
  let diff = d.getTimezoneOffset();

  const tableData = useSelector(
    (state) => state.mdrReducer?.JiraIncidentTableData
  );

  const jiraTableFilters =
    useSelector((state) => state.mdrReducer?.JiraIncidentTableFilter) || {};
  const loader = useSelector(
    (state) => state.mdrReducer?.JiraIncidentTableLoader
  );

  const [size, setSize] = useState(10);
  const [page, setPage] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentFilterField, setCurrentFilterField] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const incidentData = useSelector(
    (state) => state.IncidenetReducer?.incidentSideDrawerData
  );
  const jiraDetailsDrawer = useSelector(
    (state) => state.IncidenetReducer?.incidentDrawer
  );
  const fromTimeStamp = useSelector((state) => state.mdrReducer.fromTimeStamp);
  const toTimeStamp = useSelector((state) => state.mdrReducer.toTimeStamp);

  const [paginationAction, setPaginationAction] = useState("reset");

  useEffect(() => {
    if (fromTimeStamp && toTimeStamp) {
      dispatch({
        type: GET_TABLE_DATA_JIRA,
        payload: { size, reset: true, fromTimeStamp, toTimeStamp },
      });
      setPage(0);
      setPaginationAction(null);
    }
  }, [fromTimeStamp, toTimeStamp, size, dispatch]);

useEffect(() => {
  if (!paginationAction) return;

  const categoryFilters = jiraTableFilters.incidentCategory || [];
  const priorityFilters = jiraTableFilters.incidentPriority || [];

  const hasFilters = categoryFilters.length > 0 || priorityFilters.length > 0;

  // Build common payload base
  const basePayload = {
    size,
    page: 0,
    fromTimeStamp,
    toTimeStamp,
    categoryFilters,
    priorityFilters,
  };

  if (paginationAction === "reset") {
    // Apply filters if present
    dispatch({
      type: GET_TABLE_DATA_JIRA,
      payload: {
        ...basePayload,
        reset: true,
      },
    });
  } else if (paginationAction === "next") {
    // Keep filters when going next
    dispatch({
      type: GET_TABLE_DATA_JIRA,
      payload: {
        ...basePayload,
        hasNext: true,
      },
    });
  } else if (paginationAction === "prev") {
    // Keep filters when going previous
    dispatch({
      type: GET_TABLE_DATA_JIRA,
      payload: {
        ...basePayload,
        hasPrevious: true,
      },
    });
  }

  setPaginationAction(null);
}, [paginationAction, size, jiraTableFilters,]);

  const allRows =
    (tableData?.incidentData || []).map((row, index) => ({
      ...row,
      _rowId: index,
    })) || [];

  // Extract unique values for filters from rows
  useEffect(() => {
    const uniqueTicketIds = [...new Set(allRows.map((r) => r.ticketId))];
    const uniqueIncidentIds = [...new Set(allRows.map((r) => r.incidentId))];
    const uniquePriorities = [
      ...new Set(allRows.map((r) => r.incidentPriority?.toLowerCase())),
    ].filter(Boolean);
    const uniqueStatuses = [...new Set(allRows.map((r) => r.incidentStatus))];

    dispatch({
      type: SET_JIRA_INCIDENT_TABLE_FILTER_OPTIONS,
      payload: { options: { ticketId: uniqueTicketIds } },
    });

    dispatch({
      type: SET_JIRA_INCIDENT_TABLE_FILTER_OPTIONS,
      payload: { options: { incidentId: uniqueIncidentIds } },
    });

    dispatch({
      type: SET_JIRA_INCIDENT_TABLE_FILTER_OPTIONS,
      payload: { options: { incidentPriority: uniquePriorities } },
    });

    dispatch({
      type: SET_JIRA_INCIDENT_TABLE_FILTER_OPTIONS,
      payload: { options: { incidentStatus: uniqueStatuses } },
    });
  }, [allRows, dispatch]);

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      const matchTicketId =
        !jiraTableFilters.ticketId ||
        jiraTableFilters.ticketId.length === 0 ||
        jiraTableFilters.ticketId.includes(row.ticketId);

      const matchIncidentId =
        !jiraTableFilters.incidentId ||
        jiraTableFilters.incidentId.length === 0 ||
        jiraTableFilters.incidentId.includes(row.incidentId);

      const matchPriority =
        !jiraTableFilters.incidentPriority ||
        jiraTableFilters.incidentPriority.length === 0 ||
        jiraTableFilters.incidentPriority
          .map((v) => v.toLowerCase())
          .includes(row.incidentPriority?.toLowerCase());

      const matchStatus =
        !jiraTableFilters.incidentStatus ||
        jiraTableFilters.incidentStatus.length === 0 ||
        jiraTableFilters.incidentStatus
          .map((v) => v.toLowerCase())
          .includes(row.incidentStatus?.toLowerCase());

      // Category filter with Open/Closed logic
      const categoryFilters = jiraTableFilters.incidentCategory || [];
      let matchCategory = true;

      if (categoryFilters.length > 0) {
        // If "Open" selected → include rows whose category includes "Open"
        if (categoryFilters.includes("Open")) {
          matchCategory = row.incidentCategory?.toLowerCase().includes("open");
        }

        // If "Closed" selected → include rows that DO NOT include "Open"
        if (categoryFilters.includes("Closed")) {
          matchCategory = !row.incidentCategory?.toLowerCase().includes("open");
        }
      }

      return (
        matchTicketId &&
        matchIncidentId &&
        matchPriority &&
        matchStatus &&
        matchCategory
      );
    });
  }, [allRows, jiraTableFilters]);

  const handleFilterIconClick = (event, field) => {
    event.stopPropagation();
    if (currentFilterField === field && showFilter) {
      setShowFilter(false);
      setCurrentFilterField(null);
      setAnchorEl(null);
    } else {
      setShowFilter(true);
      setCurrentFilterField(field);
      setAnchorEl(event.currentTarget);
    }
  };

  const calculateTicketAge = (createdDate, updatedDate) => {
    if (!createdDate || !updatedDate) return "-";
    const created = dayjs(createdDate);
    const updated = dayjs(updatedDate);
    const diffDays = updated.diff(created, "day");
    if (diffDays >= 365) return `${Math.floor(diffDays / 365)}Y`;
    if (diffDays >= 30) return `${Math.floor(diffDays / 30)}M`;
    return `${diffDays}D`;
  };

  const toggleFilter = (field, value) => {
    let newFilters = [];
    const statusOptions = ["Open", "Closed"];

    // Read the latest filters directly
    const currentCategoryFilters = jiraTableFilters.incidentCategory || [];
    const currentPriorityFilters = jiraTableFilters.incidentPriority || [];

    if (field === "incidentCategory") {
      newFilters = currentCategoryFilters.includes(value) ? [] : [value];

      // Compute updated filters for API call (include current priority)
      const updatedCategory = newFilters;
      const updatedPriority = currentPriorityFilters;

      dispatch({
        type: SET_JIRA_INCIDENT_TABLE_FILTER,
        payload: { key: field, values: updatedCategory },
      });

      dispatch({
        type: GET_TABLE_DATA_JIRA,
        payload: {
          size,
          page: 0,
          categoryFilters: updatedCategory,
          priorityFilters: updatedPriority,
          fromTimeStamp,
          toTimeStamp,
          reset: true,
        },
      });

      setPage(0);
      return;
    }

    if (field === "incidentPriority") {
      const current = currentPriorityFilters;
      newFilters = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      // Compute updated filters for API call (include current category)
      const updatedCategory = currentCategoryFilters;
      const updatedPriority = newFilters;

      dispatch({
        type: SET_JIRA_INCIDENT_TABLE_FILTER,
        payload: { key: field, values: updatedPriority },
      });

      dispatch({
        type: GET_TABLE_DATA_JIRA,
        payload: {
          size,
          page: 0,
          categoryFilters: updatedCategory,
          priorityFilters: updatedPriority,
          fromTimeStamp,
          toTimeStamp,
          reset: true,
        },
      });

      setPage(0);
      return;
    }

    const currentFilters = jiraTableFilters[field] || [];
    newFilters = currentFilters.includes(value)
      ? currentFilters.filter((v) => v !== value)
      : [...currentFilters, value];

    dispatch({
      type: SET_JIRA_INCIDENT_TABLE_FILTER,
      payload: { key: field, values: newFilters },
    });
  };

  const columns = useMemo(
    () => [
      {
        field: "ticketId",
        headerName: "Ticket ID",
        flex: 1,
        sortable: true,
        renderHeader: renderHeaderWithFilter(
          "Ticket ID",
          "ticketId",
          jiraTableFilters?.ticketId?.length > 0,
          currentFilterField,
          handleFilterIconClick
        ),
        renderCell: (params) => params.value || "-",
      },
      {
        field: "incidentId",
        headerName: "Incident ID",
        flex: 1,
        sortable: true,
        renderHeader: renderHeaderWithFilter(
          "Incident ID",
          "incidentId",
          jiraTableFilters?.incidentId?.length > 0,
          currentFilterField,
          handleFilterIconClick
        ),
        renderCell: (params) => params.value || "-",
      },
      {
        field: "incidentPriority",
        headerName: "Priority",
        flex: 1,
        sortable: false,
        renderCell: (params) =>
          params.value ? (
            <span className={`priority-chip ${params.value.toLowerCase()}`}>
              {params.value}
            </span>
          ) : (
            "-"
          ),
      },
      {
        field: "incidentStatus",
        headerName: "Status",
        flex: 1.5,
        sortable: false,
        renderHeader: renderHeaderWithFilter(
          "Status",
          "incidentStatus",
          jiraTableFilters?.incidentStatus?.length > 0,
          currentFilterField,
          handleFilterIconClick,
          "jira"
        ),
        renderCell: (params) => (
          <div className="table-cell-content">{params.value || "-"}</div>
        ),
      },
      {
        field: "incidentType",
        headerName: "Type",
        flex: 1.2,
        sortable: false,
        renderCell: (params) => (
          <div className="table-cell-content">{params.value || "-"}</div>
        ),
      },
      {
        field: "assignee",
        headerName: "Assignee",
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <div className="table-cell-content">{params.value || "-"}</div>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        sortable: false,
        renderCell: (params) => (
          <div className="table-cell-content">{params.value || "-"}</div>
        ),
      },
      {
        field: "ticketAge",
        headerName: "Ticket Age",
        align: "center",
        headerAlign: "center",
        flex: 0.8,
        valueGetter: (params) =>
          calculateTicketAge(
            params.row.createdDate,
            params.row.incidentUpdatedDate
          ),
        renderCell: (params) => params.value || "-",
      },
    ],
    [jiraTableFilters, currentFilterField]
  );

  return (
    <>
      <div>
        <Box className="jira-incident-table-container">
          {loader && (
            <img
              src={Page_Loader}
              alt="loading"
              className="jiraTableLoader"
            ></img>
          )}
          <div className="jira-quick-filter-container">
            <div className="quick-filter-title">Incidents Table</div>
            <Box className="quick-filters">
              <FilterGroup
                title="Status"
                items={["Open", "Closed"]}
                field="incidentCategory"
                toggleFilter={toggleFilter}
                jiraTableFilters={jiraTableFilters}
                classes="status"
              />
              <FilterGroup
                title="Priority"
                items={["High", "Medium", "Low"]}
                field="incidentPriority"
                toggleFilter={toggleFilter}
                jiraTableFilters={jiraTableFilters}
                classes="priority"
              />
            </Box>
          </div>

          <div style={{ height: 500, width: isSmall ? "100%" : "99.5%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pageSize={size}
              getRowId={(row) => row._rowId}
              hideFooter
              rowHeight={60}
              className="custom-data-grid"
              disableColumnFilter
              disableColumnSelector
              disableColumnMenu
              disableRowSelectionOnClick
              onRowClick={(params) => {
                dispatch({ type: "INCIDENT_DRAWER_DATA", payload: params.row });
              }}
            />
          </div>

          <Box
            className="pagination-controls"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              p: 1,
            }}
          >
            <Typography variant="body2" sx={{ mr: 1 }}>
              Rows per page:
            </Typography>

            <Select
              size="small"
              value={size}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setSize(newSize);
                setPage(0);
                setPaginationAction("reset");
              }}
              sx={{ mr: 2, height: 30 }}
            >
              {[10, 25, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>

            <Typography variant="body2" sx={{ mr: 2 }}>
              {`${page * size + 1}-${(page + 1) * size}`}
            </Typography>

            <IconButton
              size="small"
              disabled={!tableData?.hasPrevious}
              onClick={() => {
                setPage((prev) => Math.max(prev - 1, 0));
                setPaginationAction("prev");
              }}
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              disabled={!tableData?.hasNext}
              onClick={() => {
                setPage((prev) => prev + 1);
                setPaginationAction("next");
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            {currentFilterField && (
              <JiraColumnFilterDropdown
                filterField={currentFilterField}
                onClose={() => {
                  setShowFilter(false);
                  setCurrentFilterField(null);
                  setAnchorEl(null);
                }}
                page={page}
                size={size}
                toggleFilter={toggleFilter}
              />
            )}
          </Popover>
        </Box>
      </div>
      <React.Fragment>
        <Drawer
          classes={{
            paper: classes.paper,
          }}
          anchor="right"
          open={jiraDetailsDrawer}
          onClose={() => dispatch({ type: "CLOSE_INCIDENT_DRAWER" })}
        >
          {incidentData && (
            <IncidentSidebar
              incidentData={incidentData}
              diff={diff}
              loc="Jira"
            />
          )}
        </Drawer>
      </React.Fragment>
    </>
  );
};

export default JiraIncidentTable;
